/*
 * The rules behind a ticket's glyphs, kept apart from the drawing so they can be checked without a
 * browser: the geometry of a Started Status's circle.
 * The pie inside the circle fills by how far along its Project's Started Statuses the Status sits,
 * so a ticket's circle fills as it moves right across the Board, and it never fills whole: a full
 * circle would read as Done.
 */

/** Where a Started Status sits among its Project's Started Statuses, as `statusGlyphOf` gives it. */
export type StatusStep = { index: number; of: number }

/**
 * How much of the pie a Started Status fills: `(index + 1) / (of + 1)`, so the first of three is a
 * quarter and the last three quarters. Half when its place is unknown.
 */
export function pieFractionOf(step?: StatusStep) {
  if (!step || step.of < 1) return 0.5
  const index = Math.min(Math.max(step.index, 0), step.of - 1)
  return (index + 1) / (step.of + 1)
}

const rounded = (n: number) => Math.round(n * 1000) / 1000

/**
 * The SVG path of a pie of radius `r` centred on `cx`,`cy`, sweeping clockwise from twelve o'clock
 * through `fraction` of the circle. Nothing at 0; a whole circle at 1, drawn as two half arcs,
 * since one arc can't end where it starts.
 */
export function piePath(fraction: number, cx: number, cy: number, r: number) {
  if (fraction <= 0) return ""
  if (fraction >= 1)
    return `M ${cx} ${cy - r} A ${r} ${r} 0 1 1 ${cx} ${cy + r} A ${r} ${r} 0 1 1 ${cx} ${cy - r} Z`
  const angle = fraction * 2 * Math.PI
  const x = rounded(cx + r * Math.sin(angle))
  const y = rounded(cy - r * Math.cos(angle))
  const large = fraction > 0.5 ? 1 : 0
  return `M ${cx} ${cy} L ${cx} ${cy - r} A ${r} ${r} 0 ${large} 1 ${x} ${y} Z`
}
